import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toUserFacingText } from "../../../shared/user-facing-error";

export type ToastKind = "success" | "error" | "info";

// 비-React 모듈(예: React Query 전역 MutationCache onError)에서도 토스트를 띄우기 위한 브리지.
// useToast() 는 Context 라 훅 밖에선 못 쓰므로, ToastProvider 가 마운트되며 자신의 show 를 여기
// 등록한다. Provider 가 아직 없으면 no-op(마운트 전 극초기 에러는 조용히 흘려보냄).
let globalEmit: ((kind: ToastKind, msg: string) => void) | null = null;

/** React 트리 밖에서 토스트를 띄운다(Provider 미마운트 시 무시). */
export function emitGlobalToast(kind: ToastKind, msg: string): void {
  globalEmit?.(kind, msg);
}

interface ToastItem {
  id: number;
  kind: ToastKind;
  msg: string;
}

/**
 * 오류 토스트의 최후 방어선(2026-10-15) — 호출부가 `toast.error(e.message)` 처럼 예외 원문을 넘겨도
 * (`Failed to fetch`·`TypeError: …`·DB 원문) 화면에는 사람 말만 뜨게 한다. 한국어 안내는 그대로 통과.
 */
const ERROR_TOAST_FALLBACK = "요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요. 계속되면 문의해 주세요.";

/** 토스트 자동 사라짐 시간(ms) */
const AUTO_DISMISS_MS = 2400;

interface ToastApi {
  success: (msg: string) => void;
  error: (msg: string) => void;
  info: (msg: string) => void;
  /** kind 를 직접 지정하고 싶을 때 */
  show: (kind: ToastKind, msg: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const KIND_CLASSES: Record<ToastKind, string> = {
  success:
    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-900/40",
  error:
    "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-900/40",
  info: "bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-900/30 dark:text-sky-200 dark:border-sky-900/40",
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const seq = useRef(0);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (kind: ToastKind, rawMsg: string) => {
      const id = ++seq.current;
      const msg = kind === "error" ? toUserFacingText(rawMsg, ERROR_TOAST_FALLBACK) : rawMsg;
      if (kind === "error" && msg !== rawMsg) console.warn("[toast] 기술 원문을 안내 문장으로 바꿈:", rawMsg);
      setToasts((prev) => [...prev, { id, kind, msg }]);
      setTimeout(() => remove(id), AUTO_DISMISS_MS);
    },
    [remove]
  );

  // 전역 브리지에 자신을 등록 — 비-React 모듈(React Query onError 등)이 emitGlobalToast 로 띄운다.
  useEffect(() => {
    globalEmit = show;
    return () => {
      if (globalEmit === show) globalEmit = null;
    };
  }, [show]);

  const api = useRef<ToastApi>({
    success: (msg) => show("success", msg),
    error: (msg) => show("error", msg),
    info: (msg) => show("info", msg),
    show,
  });
  // show 는 안정적이라 한 번만 채우면 되지만, 안전하게 매 렌더 동기화
  api.current = {
    success: (msg) => show("success", msg),
    error: (msg) => show("error", msg),
    info: (msg) => show("info", msg),
    show,
  };

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed right-4 top-4 z-999999 flex flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              onClick={() => remove(t.id)}
              className={`pointer-events-auto min-w-[16rem] max-w-sm cursor-pointer rounded-lg border px-4 py-3 text-sm shadow-theme-md ${
                KIND_CLASSES[t.kind]
              }`}
            >
              {t.msg}
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

/**
 * 전역 토스트 훅.
 *
 *   const toast = useToast();
 *   toast.success("저장했습니다");
 *   toast.error("저장에 실패했습니다");
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast 는 ToastProvider 안에서만 사용할 수 있습니다.");
  }
  return ctx;
}
